
import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { toast } from "@/components/ui/use-toast";
import { getRedirectUrl } from "@/lib/redirectUtils";

type AuthContextType = {
  user: User | null;
  session: Session | null;
  signInWithMagicLink: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUpWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
  isAdmin: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to safely parse JSON from localStorage
const getStoredAuth = () => {
  try {
    const storedUser = localStorage.getItem('authUser');
    return storedUser ? JSON.parse(storedUser) : null;
  } catch (e) {
    console.error("Error parsing stored auth:", e);
    return null;
  }
};

// Helper to clean up auth state
const cleanupAuthState = () => {
  // Remove standard auth tokens
  localStorage.removeItem('authUser');
  
  // Remove all Supabase auth keys from localStorage
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      localStorage.removeItem(key);
    }
  });
  
  // Remove from sessionStorage if in use
  Object.keys(sessionStorage || {}).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      sessionStorage.removeItem(key);
    }
  });
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Initialize with stored values if available
  const [user, setUser] = useState<User | null>(() => getStoredAuth());
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true); // Start as true
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true; // Prevent state updates on unmounted component
    setLoading(true); // Ensure loading is true while checking

    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        if (!isMounted) return;

        setSession(currentSession);
        const currentUser = currentSession?.user ?? null;
        setUser(currentUser);
        
        // Check for admin role
        const roles = currentUser?.app_metadata?.roles as string[];
        const isAdminCheck = roles?.includes('admin') ?? false;
        setIsAdmin(isAdminCheck);

        if (currentUser) {
          localStorage.setItem('authUser', JSON.stringify(currentUser));
        } else if (event === 'SIGNED_OUT') {
          localStorage.removeItem('authUser');
        }
        
        setLoading(false); // Set loading false *after* checking admin role
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      if (!isMounted) return;

      setSession(currentSession);
      const currentUser = currentSession?.user ?? null;
      setUser(currentUser);
      
      if (currentUser) {
        localStorage.setItem('authUser', JSON.stringify(currentUser));
        // Also check role on initial load
        const roles = currentUser.app_metadata?.roles as string[];
        const isAdminCheck = roles?.includes('admin') ?? false;
        setIsAdmin(isAdminCheck);
      } else {
        // No user, definitely not admin
        setIsAdmin(false);
      }
      
      setLoading(false); // Set loading false *after* checking admin role or lack thereof
    }).catch(error => {
      if (isMounted) {
        console.error("Error getting session:", error);
        setLoading(false); // Ensure loading is false even on error
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signInWithMagicLink = async (email: string) => {
    try {
      // Clean up existing auth state first
      cleanupAuthState();
      
      // Attempt global sign out
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (error) {
        // Continue even if this fails
      }
      
      // Get potential redirect URL
      const redirectUrl = getRedirectUrl();
      const currentPath = window.location.pathname;
      
      // Determine appropriate redirectTo URL
      const appUrl = window.location.origin;
      const redirectTo = `${appUrl}/auth?redirectBack=${encodeURIComponent(redirectUrl || currentPath)}`;
      
      const { error } = await supabase.auth.signInWithOtp({ 
        email,
        options: {
          emailRedirectTo: redirectTo,
        }
      });
      
      if (error) throw error;
      
      toast({
        title: "Magic link sent",
        description: "Please check your email for the login link.",
      });
      
      return;
    } catch (error) {
      toast({
        title: "Error sending magic link",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      // Clean up existing auth state first
      cleanupAuthState();
      
      // Attempt global sign out
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (error) {
        // Continue even if this fails
      }
      
      // Get potential redirect URL
      const redirectUrl = getRedirectUrl();
      const currentPath = window.location.pathname;
      
      // Determine appropriate redirectTo URL
      const appUrl = window.location.origin;
      const redirectTo = `${appUrl}/auth?redirectBack=${encodeURIComponent(redirectUrl || currentPath)}`;
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectTo,
        },
      });
      
      if (error) throw error;
    } catch (error) {
      toast({
        title: "Error signing in with Google",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signInWithPassword = async (email: string, password: string) => {
    try {
      // Clean up existing auth state first
      cleanupAuthState();
      
      // Attempt global sign out
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (error) {
        // Continue even if this fails
      }
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      toast({
        title: "Signed in successfully",
        description: "Welcome back!",
      });
      
    } catch (error) {
      toast({
        title: "Error signing in",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signUpWithPassword = async (email: string, password: string) => {
    try {
      // Clean up existing auth state first
      cleanupAuthState();
      
      // Attempt global sign out
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (error) {
        // Continue even if this fails
      }
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) throw error;
      
      toast({
        title: "Account created successfully",
        description: "Please check your email to confirm your account.",
      });
      
    } catch (error) {
      toast({
        title: "Error creating account",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Clean up existing auth state first
      cleanupAuthState();
      
      await supabase.auth.signOut();
      
      // Force page reload for a clean state
      window.location.href = '/auth';
    } catch (error) {
      toast({
        title: "Error signing out",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, signInWithMagicLink, signInWithGoogle, signInWithPassword, signUpWithPassword, signOut, loading, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
